import { useEffect, useMemo, useState } from 'react';
import { ArrowSquareOut, DownloadSimple, ShareNetwork, X } from '@phosphor-icons/react';
import { detectInstallEnvironment, isStandaloneDisplay } from '@/lib/pwaInstall';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

function InstallInstructions({ environment }: { environment: ReturnType<typeof detectInstallEnvironment> }) {
  if (environment === 'telegram') {
    return (
      <>
        <p>Во встроенном браузере приложение не устанавливается.</p>
        <ol>
          <li>Нажмите меню <b>⋯</b> или кнопку «Поделиться».</li>
          <li>Выберите «Открыть в Safari» на iPhone или «Открыть в браузере» на Android.</li>
          <li>В Safari выберите «На экран Домой», а в Chrome — «Установить приложение».</li>
        </ol>
      </>
    );
  }

  if (environment === 'ios') {
    return (
      <>
        <p>На iPhone установка выполняется через меню Safari.</p>
        <ol>
          <li>Нажмите кнопку <b>«Поделиться»</b> внизу экрана.</li>
          <li>Прокрутите меню и выберите <b>«На экран Домой»</b>.</li>
          <li>Нажмите <b>«Добавить»</b>.</li>
        </ol>
      </>
    );
  }

  return (
    <>
      <p>Откройте меню браузера и выберите «Установить приложение» или «Добавить на главный экран».</p>
      <p className="mnc-install-note">Лучше всего установка работает в Chrome или Edge на Android и компьютере.</p>
    </>
  );
}

export function PwaInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(() => isStandaloneDisplay());
  const [isOpen, setIsOpen] = useState(false);
  const environment = useMemo(
    () => detectInstallEnvironment(navigator.userAgent, navigator.platform, navigator.maxTouchPoints),
    []
  );

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      setIsOpen(false);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  if (isInstalled) return null;

  const install = async () => {
    if (!installPrompt || environment !== 'browser') {
      setIsOpen(true);
      return;
    }
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') setIsInstalled(true);
    setInstallPrompt(null);
  };

  return (
    <>
      <button
        className="mnc-install-button"
        type="button"
        aria-label="Установить приложение"
        onClick={() => void install()}
      >
        <DownloadSimple weight="bold" aria-hidden />
        <span>Установить</span>
      </button>

      {isOpen && (
        <div className="mnc-install-backdrop" role="presentation" onMouseDown={() => setIsOpen(false)}>
          <section
            className="mnc-install-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mnc-install-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              className="mnc-install-close"
              type="button"
              aria-label="Закрыть инструкцию"
              onClick={() => setIsOpen(false)}
            >
              <X weight="bold" aria-hidden />
            </button>
            <span className="mnc-install-icon" aria-hidden>
              {environment === 'telegram' ? <ArrowSquareOut weight="duotone" /> : <ShareNetwork weight="duotone" />}
            </span>
            <h2 id="mnc-install-title">
              {environment === 'telegram' ? 'Откройте сайт в браузере' : 'Установите приложение'}
            </h2>
            <InstallInstructions environment={environment} />
          </section>
        </div>
      )}
    </>
  );
}
